/**
 * Workplan Search Page - Entity-Aware Version
 * Search past workplans using workplan_document entities with comprehensive metadata
 * Includes workplan comparison, reusable content library, and project analysis
 * Uses generic listEntities() - NO custom backend endpoints
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, Select, Tabs } from '@tracekit/core';
import { useWorkspace, listEntities } from '@tracekit/core';
import { 
  Search, Filter, Download, Grid, List, CheckCircle, Plus, Calendar, 
  Building2, DollarSign, FileText, Clock, Target, AlertTriangle,
  ChevronDown, ChevronRight, Copy, Eye, BarChart3, X
} from 'lucide-react';
import { useEvidenceBasket } from '../components/EvidenceBasket';
import { useToastStore } from '@tracekit/core';
import { Breadcrumbs } from '../components/Breadcrumbs';

// Entity type from Policy Pack v2.0 - workplan_document
interface WorkplanEntity {
  id: string;
  entity_type: string;
  data: {
    workplan_id?: string;
    artifact_id?: string;
    project_name: string;
    client_name: string;
    project_type?: string;
    service_line?: string;
    scope_summary?: string;
    methodology?: string;
    timeline_weeks?: number;
    key_milestones?: string[];
    deliverables?: string[];
    team_roles?: string[];
    budget_estimate?: number;
    risk_factors?: string[];
    assumptions?: string[];
    dependencies?: string[];
    equipment_requirements?: string[];
    safety_considerations?: string;
  };
  created_at?: string;
}

interface FilterState {
  dateFrom: string;
  dateTo: string;
  client: string;
  projectType: string;
  serviceLine: string;
  minBudget: string;
  maxBudget: string;
  minTimeline: string;
  maxTimeline: string;
}

export function WorkplanSearchPage() {
  const { projectId } = useWorkspace();
  const { basket, addEvidence } = useEvidenceBasket();
  const { addToast } = useToastStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'comparison'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: '',
    dateTo: '',
    client: '',
    projectType: '',
    serviceLine: 'all',
    minBudget: '',
    maxBudget: '',
    minTimeline: '',
    maxTimeline: '',
  });
  const [selectedResults, setSelectedResults] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [comparisonWorkplans, setComparisonWorkplans] = useState<WorkplanEntity[]>([]);
  const [activeTab, setActiveTab] = useState('search');

  // Fetch all workplan entities using generic listEntities (NO custom endpoint)
  const { data: allWorkplans, isLoading: workplansLoading, error: workplansError } = useQuery({
    queryKey: ['workplan-entities', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      // Use generic listEntities - same pattern as ProposalSearchPage
      const entities = await listEntities(projectId, 'workplan_document', 1000);
      return entities.map(e => ({
        id: e.id,
        entity_type: e.entity_type || 'workplan_document',
        data: (e.data || {}) as WorkplanEntity['data'],
        created_at: e.created_at || new Date().toISOString(),
      })) as WorkplanEntity[];
    },
    enabled: !!projectId,
  });

  // Check which results are already in basket
  const basketArtifactIds = useMemo(() => {
    return new Set(basket.map(item => item.artifact_id));
  }, [basket]);

  // Filtered and searched results (client-side filtering - NO backend search endpoint)
  const filteredResults = useMemo(() => {
    if (!allWorkplans) return [];
    
    return allWorkplans.filter((workplan: WorkplanEntity) => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          workplan.data.project_name?.toLowerCase().includes(searchLower) ||
          workplan.data.client_name?.toLowerCase().includes(searchLower) ||
          workplan.data.project_type?.toLowerCase().includes(searchLower) ||
          workplan.data.service_line?.toLowerCase().includes(searchLower) ||
          workplan.data.scope_summary?.toLowerCase().includes(searchLower) ||
          workplan.data.methodology?.toLowerCase().includes(searchLower) ||
          workplan.data.deliverables?.some(d => d.toLowerCase().includes(searchLower)) ||
          workplan.data.key_milestones?.some(m => m.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }
      
      // Date filters
      if (filters.dateFrom && workplan.created_at) {
        if (new Date(workplan.created_at) < new Date(filters.dateFrom)) {
          return false;
        }
      }
      if (filters.dateTo && workplan.created_at) {
        if (new Date(workplan.created_at) > new Date(filters.dateTo)) {
          return false;
        }
      }
      
      // Client filter
      if (filters.client && workplan.data.client_name) {
        if (!workplan.data.client_name.toLowerCase().includes(filters.client.toLowerCase())) {
          return false;
        }
      }
      
      // Project type filter
      if (filters.projectType && workplan.data.project_type) {
        if (workplan.data.project_type !== filters.projectType) {
          return false;
        }
      }
      
      // Service line filter
      if (filters.serviceLine && filters.serviceLine !== 'all' && workplan.data.service_line) {
        if (workplan.data.service_line !== filters.serviceLine) {
          return false;
        }
      }
      
      // Budget filters
      if (filters.minBudget && workplan.data.budget_estimate) {
        if (workplan.data.budget_estimate < parseFloat(filters.minBudget)) {
          return false;
        }
      }
      if (filters.maxBudget && workplan.data.budget_estimate) {
        if (workplan.data.budget_estimate > parseFloat(filters.maxBudget)) {
          return false;
        }
      }
      
      // Timeline filters
      if (filters.minTimeline && workplan.data.timeline_weeks) {
        if (workplan.data.timeline_weeks < parseFloat(filters.minTimeline)) {
          return false;
        }
      }
      if (filters.maxTimeline && workplan.data.timeline_weeks) {
        if (workplan.data.timeline_weeks > parseFloat(filters.maxTimeline)) {
          return false;
        }
      }
      
      return true;
    });
  }, [allWorkplans, searchQuery, filters]);

  // Get unique values for filter dropdowns
  const uniqueClients = useMemo(() => {
    if (!allWorkplans) return [];
    return Array.from(new Set(allWorkplans.map(w => w.data.client_name).filter(Boolean))).sort();
  }, [allWorkplans]);

  const uniqueProjectTypes = useMemo(() => {
    if (!allWorkplans) return [];
    return Array.from(new Set(allWorkplans.map(w => w.data.project_type).filter(Boolean))).sort();
  }, [allWorkplans]);

  const uniqueServiceLines = useMemo(() => {
    if (!allWorkplans) return [];
    return Array.from(new Set(allWorkplans.map(w => w.data.service_line).filter(Boolean))).sort();
  }, [allWorkplans]);

  const handleAddToBasket = (workplan: WorkplanEntity) => {
    if (!workplan.data.artifact_id) {
      addToast({
        type: 'warning',
        title: 'Cannot add to basket',
        message: 'Workplan does not have an artifact ID',
      });
      return;
    }
    
    addEvidence({
      artifact_id: workplan.data.artifact_id,
      entity_id: workplan.id,
      entity_type: 'workplan_document',
      title: workplan.data.project_name || 'Untitled Workplan',
      excerpt: workplan.data.scope_summary || '',
      metadata: {
        client_name: workplan.data.client_name,
        service_line: workplan.data.service_line,
        project_type: workplan.data.project_type,
      },
    });
  };

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleAddToComparison = (workplan: WorkplanEntity) => {
    if (comparisonWorkplans.length >= 3) {
      addToast({
        type: 'warning',
        title: 'Comparison limit reached',
        message: 'You can compare up to 3 workplans at a time',
      });
      return;
    }
    
    if (comparisonWorkplans.find(w => w.id === workplan.id)) {
      addToast({
        type: 'info',
        title: 'Already in comparison',
        message: 'This workplan is already added to comparison',
      });
      return;
    }
    
    setComparisonWorkplans([...comparisonWorkplans, workplan]);
    addToast({
      type: 'success',
      title: 'Added to comparison',
      message: `${workplan.data.project_name} added to comparison view`,
    });
  };

  const handleRemoveFromComparison = (workplanId: string) => {
    setComparisonWorkplans(comparisonWorkplans.filter(w => w.id !== workplanId));
  };

  const handleBulkAdd = () => {
    const count = filteredResults.filter((r: WorkplanEntity) => selectedResults.has(r.id)).length;
    filteredResults
      .filter((r: WorkplanEntity) => selectedResults.has(r.id))
      .forEach((result: WorkplanEntity) => handleAddToBasket(result));
    setSelectedResults(new Set());
    addToast({
      type: 'success',
      title: 'Added to evidence basket',
      message: `${count} workplan${count !== 1 ? 's' : ''} added to your evidence basket`,
    });
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (format === 'csv') {
      const headers = ['Project Name', 'Client', 'Service Line', 'Project Type', 'Timeline (Weeks)', 'Budget Estimate', 'Deliverables', 'Key Milestones'];
      const rows = filteredResults.map((r: WorkplanEntity) => [
        r.data.project_name || 'N/A',
        r.data.client_name || 'N/A',
        r.data.service_line || 'N/A',
        r.data.project_type || 'N/A',
        r.data.timeline_weeks?.toString() || 'N/A',
        r.data.budget_estimate?.toString() || 'N/A',
        r.data.deliverables?.join('; ') || 'N/A',
        r.data.key_milestones?.join('; ') || 'N/A',
      ]);
      const csv = [headers, ...rows].map((row: (string | number)[]) => row.map((cell: string | number) => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workplan-search-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else {
      const blob = new Blob([JSON.stringify(filteredResults, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workplan-search-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    }
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      client: '',
      projectType: '',
      serviceLine: 'all',
      minBudget: '',
      maxBudget: '',
      minTimeline: '',
      maxTimeline: '',
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all').length;

  const handleAddToBasketWithFeedback = (workplan: WorkplanEntity) => {
    handleAddToBasket(workplan);
    addToast({
      type: 'success',
      title: 'Added to evidence basket',
      message: `${workplan.data.project_name} has been added to your evidence basket`,
    });
  };

  if (workplansError) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-6">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Error Loading Workplans</h2>
            <p className="text-gray-600">{workplansError.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Breadcrumbs items={[
        { label: 'Workplans', path: '/rfp/workplan-execution' },
        { label: 'Search Workplans' },
      ]} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Search Past Workplans</h1>
        <p className="text-gray-600 mt-1">Find relevant past workplans for evidence and inspiration</p>
      </div>
      
      {/* Search Bar */}
      <Card className="mb-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search workplans by project name, client, service line, methodology..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="default" className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : viewMode === 'list' ? 'comparison' : 'grid')}
            >
              {viewMode === 'grid' && <List className="h-4 w-4 mr-2" />}
              {viewMode === 'list' && <Grid className="h-4 w-4 mr-2" />}
              {viewMode === 'comparison' && <BarChart3 className="h-4 w-4 mr-2" />}
              {viewMode === 'grid' ? 'List' : viewMode === 'list' ? 'Compare' : 'Grid'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('json')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-1">Client</label>
                <Input
                  type="text"
                  placeholder="Filter by client"
                  value={filters.client}
                  onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Project Type</label>
                <Select
                  value={filters.projectType}
                  onValueChange={(value) => setFilters({ ...filters, projectType: value })}
                >
                  <option value="">All Types</option>
                  {uniqueProjectTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Service Line</label>
                <Select
                  value={filters.serviceLine}
                  onValueChange={(value) => setFilters({ ...filters, serviceLine: value })}
                >
                  <option value="all">All Service Lines</option>
                  {uniqueServiceLines.map(line => (
                    <option key={line} value={line}>{line}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date From</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date To</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Min Budget (CAD)</label>
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.minBudget}
                  onChange={(e) => setFilters({ ...filters, minBudget: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Budget (CAD)</label>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.maxBudget}
                  onChange={(e) => setFilters({ ...filters, maxBudget: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Min Timeline (Weeks)</label>
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.minTimeline}
                  onChange={(e) => setFilters({ ...filters, minTimeline: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Timeline (Weeks)</label>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.maxTimeline}
                  onChange={(e) => setFilters({ ...filters, maxTimeline: e.target.value })}
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedResults.size > 0 && (
        <Card className="mb-6 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {selectedResults.size} workplan{selectedResults.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkAdd}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Evidence Basket
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedResults(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Loading */}
      {workplansLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Results */}
      {!workplansLoading && allWorkplans && (
        <>
          {/* Comparison View */}
          {viewMode === 'comparison' && comparisonWorkplans.length > 0 && (
            <Card className="overflow-x-auto mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Workplan Comparison</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setComparisonWorkplans([])}
                >
                  Clear Comparison
                </Button>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Field</th>
                    {comparisonWorkplans.map((workplan) => (
                      <th key={workplan.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-between">
                          <span className="truncate max-w-xs">{workplan.data.project_name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromComparison(workplan.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">Client</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{w.data.client_name || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Service Line</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{w.data.service_line || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Project Type</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{w.data.project_type || 'N/A'}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Timeline (Weeks)</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {w.data.timeline_weeks ? `${w.data.timeline_weeks} weeks` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Budget Estimate</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {w.data.budget_estimate ? `$${w.data.budget_estimate.toLocaleString()}` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Deliverables</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 text-sm text-gray-700">
                        {w.data.deliverables?.length ? (
                          <ul className="list-disc pl-4 space-y-1">
                            {w.data.deliverables.slice(0, 3).map((d, i) => (
                              <li key={i}>{d}</li>
                            ))}
                            {w.data.deliverables.length > 3 && <li>... and {w.data.deliverables.length - 3} more</li>}
                          </ul>
                        ) : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Key Milestones</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 text-sm text-gray-700">
                        {w.data.key_milestones?.length ? (
                          <ul className="list-disc pl-4 space-y-1">
                            {w.data.key_milestones.slice(0, 3).map((m, i) => (
                              <li key={i}>{m}</li>
                            ))}
                            {w.data.key_milestones.length > 3 && <li>... and {w.data.key_milestones.length - 3} more</li>}
                          </ul>
                        ) : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Scope Summary</td>
                    {comparisonWorkplans.map((w) => (
                      <td key={w.id} className="px-6 py-4 text-sm text-gray-700">
                        {w.data.scope_summary ? (
                          <p className="line-clamp-3">{w.data.scope_summary}</p>
                        ) : 'N/A'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </Card>
          )}

          {/* Results Count */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredResults.length} of {allWorkplans.length} workplan{allWorkplans.length !== 1 ? 's' : ''}
            </p>
            {comparisonWorkplans.length > 0 && viewMode !== 'comparison' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode('comparison')}
              >
                View Comparison ({comparisonWorkplans.length})
              </Button>
            )}
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResults.map((workplan: WorkplanEntity) => (
                <Card key={workplan.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1 line-clamp-2">
                        {workplan.data.project_name || 'Untitled Workplan'}
                      </h3>
                      <p className="text-sm text-gray-600">{workplan.data.client_name}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedResults.has(workplan.id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedResults);
                        if (e.target.checked) {
                          newSelected.add(workplan.id);
                        } else {
                          newSelected.delete(workplan.id);
                        }
                        setSelectedResults(newSelected);
                      }}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {workplan.data.service_line && (
                      <Badge variant="secondary">{workplan.data.service_line}</Badge>
                    )}
                    {workplan.data.project_type && (
                      <Badge variant="outline">{workplan.data.project_type}</Badge>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    {workplan.data.timeline_weeks && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>{workplan.data.timeline_weeks} weeks</span>
                      </div>
                    )}
                    {workplan.data.budget_estimate && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        <span>${workplan.data.budget_estimate.toLocaleString()}</span>
                      </div>
                    )}
                    {workplan.data.deliverables && workplan.data.deliverables.length > 0 && (
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>{workplan.data.deliverables.length} deliverable{workplan.data.deliverables.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  {workplan.data.scope_summary && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {workplan.data.scope_summary}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddToBasketWithFeedback(workplan)}
                      disabled={!workplan.data.artifact_id || basketArtifactIds.has(workplan.data.artifact_id)}
                    >
                      {basketArtifactIds.has(workplan.data.artifact_id) ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          In Basket
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Add to Basket
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddToComparison(workplan)}
                      disabled={comparisonWorkplans.length >= 3 || comparisonWorkplans.some(w => w.id === workplan.id)}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Compare
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRow(workplan.id)}
                    >
                      {expandedRows.has(workplan.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Expanded Details */}
                  {expandedRows.has(workplan.id) && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      {workplan.data.methodology && (
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Methodology</h4>
                          <p className="text-sm text-gray-600">{workplan.data.methodology}</p>
                        </div>
                      )}
                      {workplan.data.key_milestones && workplan.data.key_milestones.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Key Milestones</h4>
                          <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                            {workplan.data.key_milestones.map((milestone, i) => (
                              <li key={i}>{milestone}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {workplan.data.deliverables && workplan.data.deliverables.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Deliverables</h4>
                          <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                            {workplan.data.deliverables.map((deliverable, i) => (
                              <li key={i}>{deliverable}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {workplan.data.risk_factors && workplan.data.risk_factors.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Risk Factors</h4>
                          <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                            {workplan.data.risk_factors.map((risk, i) => (
                              <li key={i}>{risk}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {workplan.data.team_roles && workplan.data.team_roles.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Team Roles</h4>
                          <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                            {workplan.data.team_roles.map((role, i) => (
                              <li key={i}>{role}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <Card>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={filteredResults.length > 0 && filteredResults.every(r => selectedResults.has(r.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedResults(new Set(filteredResults.map(r => r.id)));
                          } else {
                            setSelectedResults(new Set());
                          }
                        }}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Line</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timeline</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredResults.map((workplan: WorkplanEntity) => (
                    <tr key={workplan.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedResults.has(workplan.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedResults);
                            if (e.target.checked) {
                              newSelected.add(workplan.id);
                            } else {
                              newSelected.delete(workplan.id);
                            }
                            setSelectedResults(newSelected);
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {workplan.data.project_name || 'Untitled Workplan'}
                        </div>
                        {workplan.data.scope_summary && (
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {workplan.data.scope_summary}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {workplan.data.client_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {workplan.data.service_line && (
                          <Badge variant="secondary">{workplan.data.service_line}</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {workplan.data.timeline_weeks ? `${workplan.data.timeline_weeks} weeks` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {workplan.data.budget_estimate ? `$${workplan.data.budget_estimate.toLocaleString()}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAddToBasketWithFeedback(workplan)}
                            disabled={!workplan.data.artifact_id || basketArtifactIds.has(workplan.data.artifact_id)}
                            title={basketArtifactIds.has(workplan.data.artifact_id) ? 'Already in basket' : 'Add to evidence basket'}
                          >
                            {basketArtifactIds.has(workplan.data.artifact_id) ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <Plus className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAddToComparison(workplan)}
                            disabled={comparisonWorkplans.length >= 3 || comparisonWorkplans.some(w => w.id === workplan.id)}
                            title="Add to comparison"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRow(workplan.id)}
                          >
                            {expandedRows.has(workplan.id) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Empty State */}
          {filteredResults.length === 0 && (
            <Card className="p-12">
              <div className="text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No workplans found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery || activeFilterCount > 0
                    ? 'Try adjusting your search or filters'
                    : 'No workplans available in this project'}
                </p>
                {(searchQuery || activeFilterCount > 0) && (
                  <Button variant="outline" onClick={() => {
                    setSearchQuery('');
                    handleClearFilters();
                  }}>
                    Clear Search & Filters
                  </Button>
                )}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
